import React, { Fragment, useMemo, useState } from 'react';
import Swal from 'sweetalert2';

import { useAuth } from '../../Context/Context';

import SideNav from '../../Components/SideNav';
import Loader from '../../Components/Loader';
import ListaAdmin from '../../Components/ListaAdmin';
import FilaAdmin, { Insignia } from '../../Components/FilaAdmin';
import PanelEdicion from '../../Components/PanelEdicion';
import SeccionPanel from '../../Components/SeccionPanel';

import useListaAdmin from '../../Hooks/useListaAdmin';

import adminsHelper from '../../Helpers/Admin';
import { actualizarDatosSesion, leerDatosSesion } from '../../Helpers/sesion';
import { ADMIN_GENERAL, TIPOS_ADMIN, etiquetaTipo } from '../../Helpers/tiposAdmin';

const VACIO = { tipoMostrar: '', correo: '', tipo: '1', password: '', confirmacion: '' };
const CAMPOS_BUSQUEDA = ['tipoMostrar', 'correo'];

const estiloAyuda = { color: "#757575", fontSize: "13px", margin: "-10px 0 10px" };

// Primero por tipo (el general arriba) y luego por nombre
const ordenar = (a, b) => String(a.tipo).localeCompare(String(b.tipo)) || String(a.tipoMostrar).localeCompare(String(b.tipoMostrar), 'es');

const iniciales = nombre => String(nombre ?? '').split(/\s+/).filter(Boolean).slice(0, 2).map(palabra => palabra[0].toUpperCase()).join('');

const Administradores = () => {
    const { recargar, setUser } = useAuth();
    const [guardando, setGuardando] = useState(false);

    // Se lee directo de la sesión: el Context se llena hasta que monta el SideNav
    const sesion = useMemo(() => leerDatosSesion(), []);
    const esGeneral = sesion?.tipo === ADMIN_GENERAL;

    const l = useListaAdmin({ obtener: adminsHelper.obtenerAdmins, vacio: VACIO, camposBusqueda: CAMPOS_BUSQUEDA, ordenar });

    const agregando = l.panel.abierto === 'agregar';
    // Igual que la API: nadie cambia su propio tipo ni se elimina a sí mismo
    const esPropio = Boolean(l.editar) && l.editar._id === sesion?.id;
    const b = l.borrador;

    const errores = () => {
        const faltantes = [
            [b.tipoMostrar.trim(), 'Nombre'],
            [b.correo.trim(), 'Correo'],
            [!agregando || b.password, 'Contraseña']
        ].filter(([valor]) => !valor).map(([, nombre]) => nombre);
        if (faltantes.length) return 'Faltan datos por llenar: ' + faltantes.join(', ');
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(b.correo.trim())) return 'El correo no es válido';
        const repetido = l.todos.find(admin => admin._id !== l.editar?._id && admin.correo.toLowerCase() === b.correo.trim().toLowerCase());
        if (repetido) return 'Ya existe un administrador con ese correo';
        if (b.password || b.confirmacion) {
            if (b.password.length < 8) return 'La contraseña debe tener al menos 8 caracteres';
            if (b.password !== b.confirmacion) return 'Las contraseñas no coinciden';
        }
        return null;
    }

    const guardar = async e => {
        e.preventDefault();
        const error = errores();
        if (error) return Swal.fire('Error!', error, 'error');
        setGuardando(true);
        const datos = { tipoMostrar: b.tipoMostrar.trim(), correo: b.correo.trim(), password: b.password };
        const res = agregando
            ? await adminsHelper.agregarAdmin({ ...datos, tipo: b.tipo })
            : await adminsHelper.editarAdmin({ ...datos, tipo: esPropio ? undefined : b.tipo }, l.editar._id);
        if (!res?.success) return setGuardando(false);
        // El header lee el nombre de la sesión: se actualiza para no esperar a volver a iniciar sesión
        if (esPropio) setUser(actualizarDatosSesion({ nombre: datos.tipoMostrar, puesto: datos.tipoMostrar }));
        recargar();
    }

    const eliminar = async () => {
        const { isConfirmed } = await Swal.fire({
            icon: 'question',
            titleText: '¿Eliminar a ' + l.editar.tipoMostrar + '?',
            text: 'Ya no podrá iniciar sesión en el panel.',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });
        if (!isConfirmed) return;
        setGuardando(true);
        const res = await adminsHelper.eliminarAdmin(l.editar._id);
        if (res?.success) return recargar();
        setGuardando(false);
    }

    return (
        <Fragment>
            <SideNav />
            {guardando && (
                <div style={{width:"100%", height:"100vh", position:"fixed", top:"0", background:"#00000059", zIndex:"9999"}}>
                    <Loader />
                </div>
            )}
            <div className='row'>
                <PanelEdicion
                    abierto={Boolean(l.panel.abierto)}
                    titulo={agregando ? 'Agregar administrador' : esPropio ? 'Mi cuenta' : 'Editar administrador'}
                    ancho={l.panel.ancho}
                    onArrastre={l.panel.iniciarArrastre}
                    onCerrar={() => l.panel.cerrar()}
                    hayCambios={l.hayCambios}
                    pie={
                        <Fragment>
                            {!agregando && !esPropio && (
                                <button type='button' className='boton_admin boton_admin_peligro' onClick={eliminar}>
                                    Eliminar
                                </button>
                            )}
                            <button type='submit' form='form_admin' className='boton_admin' disabled={!l.hayCambios}>
                                {agregando ? 'Agregar administrador' : 'Guardar cambios'}
                            </button>
                        </Fragment>
                    }
                >
                    {l.panel.abierto && (
                        <form id='form_admin' key={l.version} onSubmit={guardar} autoComplete='off'>
                            <SeccionPanel id='cuenta' numero={1} titulo='Cuenta'>
                                <div className="input-field">
                                    <input onChange={e => l.cambiar({ tipoMostrar: e.target.value })} value={b.tipoMostrar} id='admin_nombre' type="text" maxLength={100} autoFocus />
                                    <label className='active' htmlFor='admin_nombre'>Nombre</label>
                                </div>
                                <p style={estiloAyuda}>Es el nombre que aparece en el header del panel.</p>
                                <div className="input-field">
                                    <input onChange={e => l.cambiar({ correo: e.target.value })} value={b.correo} id='admin_correo' type="email" />
                                    <label className='active' htmlFor='admin_correo'>Correo</label>
                                </div>
                                <p style={estiloAyuda}>Con este correo inicia sesión.</p>
                            </SeccionPanel>

                            <SeccionPanel id='tipo' numero={2} titulo='Tipo de usuario' ubicacion='Define qué secciones ve en el menú.'>
                                {esPropio ? (
                                    <Fragment>
                                        <p style={{ margin: "0 0 4px" }}>{etiquetaTipo(b.tipo)}</p>
                                        <p style={{ ...estiloAyuda, margin: "0 0 6px" }}>No puedes cambiar tu propio tipo; pídeselo a otro administrador general.</p>
                                    </Fragment>
                                ) : (
                                    <select className='browser-default' value={b.tipo} onChange={e => l.cambiar({ tipo: e.target.value })} aria-label='Tipo de usuario' style={{marginBottom:"6px"}}>
                                        {TIPOS_ADMIN.map(({ valor, etiqueta }) => (
                                            <option key={valor} value={valor}>{etiqueta}</option>
                                        ))}
                                    </select>
                                )}
                            </SeccionPanel>

                            <SeccionPanel id='password' numero={3} titulo='Contraseña'>
                                <div className="input-field">
                                    <input onChange={e => l.cambiar({ password: e.target.value })} value={b.password} id='admin_password' type="password" autoComplete="new-password" />
                                    <label className='active' htmlFor='admin_password'>{agregando ? 'Contraseña' : 'Nueva contraseña'}</label>
                                </div>
                                <div className="input-field">
                                    <input onChange={e => l.cambiar({ confirmacion: e.target.value })} value={b.confirmacion} id='admin_confirmacion' type="password" autoComplete="new-password" />
                                    <label className='active' htmlFor='admin_confirmacion'>Confirmar contraseña</label>
                                </div>
                                <p style={estiloAyuda}>{agregando ? 'Mínimo 8 caracteres.' : 'Déjala vacía para conservar la actual. Mínimo 8 caracteres.'}</p>
                            </SeccionPanel>
                        </form>
                    )}
                </PanelEdicion>
                <ListaAdmin
                    etiqueta='Administración'
                    encabezado='Administradores'
                    ayuda={esGeneral ? 'Haz clic en un administrador para editarlo.' : 'Usuarios con acceso a este panel.'}
                    aviso={esGeneral ? null : 'Solo un administrador general puede agregar o editar administradores.'}
                    acciones={esGeneral && (
                        <button type='button' className='boton_admin' onClick={l.abrirAgregar}>
                            + Agregar administrador
                        </button>
                    )}
                    busqueda={l.busqueda}
                    onBusqueda={l.setBusqueda}
                    placeholder='Buscar por nombre o correo'
                    total={l.todos?.length ?? 0}
                    mostrados={l.lista?.length ?? 0}
                    cargando={l.cargando}
                    textoVacio='Aún no hay administradores.'
                    margen={l.panel.abierto ? l.panel.ancho : 0}
                >
                    {l.lista?.map(admin => (
                        <FilaAdmin
                            key={admin._id}
                            activa={l.editar?._id === admin._id}
                            onClick={esGeneral ? () => l.abrirEditar(admin) : undefined}
                            iniciales={iniciales(admin.tipoMostrar)}
                            titulo={admin.tipoMostrar}
                            subtitulo={admin.correo}
                            insignias={
                                <Fragment>
                                    {admin._id === sesion?.id && <Insignia variante='verde'>Tú</Insignia>}
                                    <Insignia variante={admin.tipo === ADMIN_GENERAL ? null : 'gris'}>{etiquetaTipo(admin.tipo)}</Insignia>
                                </Fragment>
                            }
                        />
                    ))}
                </ListaAdmin>
            </div>
        </Fragment>
    );
};

export default Administradores;
